import { Box, IconButton } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { ColumnDef, Row } from "@tanstack/react-table"
import type { FC } from "react"
import { useState } from "react"

import { AlternateProductCell } from "./AlternateProductCell"
import { DistributorCell } from "./DistributorCell"
import { ManufacturerCell } from "./ManufacturerCell"
import { SupplierCell } from "./SupplierCell"

import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { PermissionActionType } from "@/constants/permissions"
import type { CompetitorProduct, Product } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import {
  type AssociatedProduct,
  getDistributors,
  getManufacturer,
  getProductName,
  getSupplier,
  isAkrochemProduct,
} from "@/utils/equivalentProducts"

interface AssociatedProductsTableProps {
  akrochemProducts: Product[]
  competitorProducts: CompetitorProduct[]
  onRemove: (product: AssociatedProduct) => void
  onRowClick?: () => void
}

export const AssociatedProductsTable: FC<AssociatedProductsTableProps> = ({
  akrochemProducts,
  competitorProducts,
  onRemove,
  onRowClick,
}) => {
  const navigate = useNavigate()
  const { hasPermissions } = usePermissionManager()
  const [productToRemove, setProductToRemove] = useState<AssociatedProduct | null>(null)

  const hasEquivalentMaintenancePermission = hasPermissions([PermissionActionType.EQUIVALENT_MAINTENANCE])
  const products: AssociatedProduct[] = [...akrochemProducts, ...competitorProducts]

  const handleRowClick = (row: Row<AssociatedProduct>) => {
    const product = row.original
    if (isAkrochemProduct(product)) {
      onRowClick?.()
      navigate({ to: `/app/product/${product.productId}/edit/information` })
    }
    // Competitor products don't navigate anywhere
  }

  const handleRemoveClick = (product: AssociatedProduct) => {
    setProductToRemove(product)
  }

  const handleConfirmRemove = () => {
    if (productToRemove) {
      onRemove(productToRemove)
      setProductToRemove(null)
    }
  }

  const handleCancelRemove = () => {
    setProductToRemove(null)
  }

  const columns: ColumnDef<AssociatedProduct>[] = [
    {
      header: "Alternate Product",
      cell: ({ row }) => <AlternateProductCell product={row.original} />,
      size: 300,
      enableSorting: false,
    },
    {
      header: "Manufacturer",
      cell: ({ row }) => <ManufacturerCell manufacturer={getManufacturer(row.original)} />,
      size: 200,
      enableSorting: false,
    },
    {
      header: "Distributor",
      cell: ({ row }) => <DistributorCell distributors={getDistributors(row.original)} />,
      size: 200,
      enableSorting: false,
    },
    {
      header: "Supplier",
      cell: ({ row }) => <SupplierCell supplier={getSupplier(row.original)} />,
      size: 120,
      enableSorting: false,
    },
    ...(hasEquivalentMaintenancePermission
      ? [
          {
            header: "",
            accessorKey: "action",
            cell: ({ row }) => {
              return (
                <Box className='flex items-center justify-center'>
                  <IconButton
                    color='primary'
                    aria-label='remove product'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveClick(row.original)
                    }}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </Box>
              )
            },
            size: 80,
            enableSorting: false,
          } as ColumnDef<AssociatedProduct>,
        ]
      : []),
  ]

  return (
    <>
      <AerosBaseTable
        slotProps={{
          bodyRow: {
            className: "hover:bg-primary-100 cursor-pointer",
            onClick: (row) => {
              if ("original" in row) {
                handleRowClick(row)
              }
            },
          },
        }}
        features={{
          tanstackOptions: {
            data: products,
            columns,
          },
        }}
        noDataMessage='No products associated'
      />

      {productToRemove && (
        <ConfirmationDialog
          title={`Are you sure you want to remove ${getProductName(productToRemove)}?`}
          content='This action cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          isWarning
          onClose={handleCancelRemove}
          onConfirm={handleConfirmRemove}
        />
      )}
    </>
  )
}
